import { useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { FC, lazy, Suspense, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from 'web-library/stories/atom/Box/Box';
import ErrorInfo from 'web-library/stories/molecule/ErrorInfo/ErrorInfo';
import Grid from 'web-library/stories/atom/Grid/Grid';
import Typography from 'web-library/stories/atom/Typography/Typography';
import WidthGuard, { useWithGuardStyles } from 'web-library/stories/atom/WidthGuard';
import Button from 'web-library/stories/atom/Button/Button';
import { AnnouncementDetail } from 'web-library/models/Announcements';
import SeabLogo from 'web-library/stories/assets/images/seab_logo.svg';
import SeabLogoWithLabel from 'web-library/stories/assets/images/seab_logo_with_label.svg';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useGetSingPassConsent } from '../../../api-refactored/user/useGetSingPassConsent';
import LoginContentImageWithOverlay from '../../../assets/content_login.svg';
import LoginContentImage from '../../../assets/illustration.svg';
import { useHTMLFontSize } from '../../../hooks/fontSize';
import { useAuthNavigation } from '../../../routes/AuthRoute';
import RouteKey from '../../../routes/key';
import { useLoginForm } from '../logic/useLoginForm';
import ExistingAccountLoginForm from './ExistingAccountLoginForm';
import { useGetMIMSAuth } from '../../../api-refactored/user/useGetMIMSAuth';
import { useGetMIMSStatus } from '../../../api/user/useGetMIMSStatus';
import { typeTheme } from 'web-library/theme/helper/tssHelper';
import SingpassLogo from '../../../assets/singpass_logo_white.svg';
import { useGetSchoolCandidateAppealPaymentOpenPeriod } from '../../../api-refactored/school-candidate-payment/useGetSchoolCandidateAppealPaymentOpenPeriod';
import PaymentAnnouncementContent from '../../announcement/components/PaymentAnnouncementContent';

const OIDCLoginButton = lazy(() => import('web-library/stories/atom/Button/OIDCLoginButton'));
const Link = lazy(() => import('web-library/stories/atom/Link/Link'));
const IconButton = lazy(() => import('web-library/stories/atom/IconButton/IconButton'));
const Dialog = lazy(() => import('web-library/stories/atom/Dialog/Dialog'));
const DialogContent = lazy(
  () => import('web-library/stories/molecule/DialogContent/DialogContent')
);
const DialogTitle = lazy(() => import('web-library/stories/molecule/DialogTitle/DialogTitle'));
const AnnouncementSingleContent = lazy(
  () => import('../../announcement/components/AnnouncementSingleContent')
);
const RootFontControl = lazy(
  () => import('web-library/stories/molecule/RootFontControl/RootFontControl')
);

const useStyles = makeStyles()((theme) => ({
  passFlex: {
    flex: 1,
    display: 'flex',
    minHeight: 870,
    flexDirection: 'column',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'row',

    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(7),

    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      flexDirection: 'column',
    },
  },
  loginContainer: {},
  loginForm: {
    maxWidth: 550,
  },
  logo: {
    marginBottom: theme.spacing(7),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  sectionText: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  orContainer: {
    height: theme.spacing(3),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  orWordContainer: {
    display: 'flex',
    '&::before': {
      position: 'relative',
      width: '100%',
      borderTop: `2px solid ${theme.color.border.borderMain.substr(0, 7).concat('40')}`,
      top: '50%',
      content: '""',
      transform: 'translateY(50%)',
    },
    '&::after': {
      position: 'relative',
      width: '100%',
      borderTop: `2px solid ${theme.color.border.borderMain.substr(0, 7).concat('40')}`,
      top: '50%',
      content: '""',
      transform: 'translateY(50%)',
    },
  },
  orWord: {
    fontWeight: 700,
    color: theme.color.text.textHeader,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontSize: '1.125rem',
    fontStyle: 'normal',
    letterSpacing: '0.019rem',
  },
  description: {
    ...typeTheme(theme, 'body2bold'),
  },
  description2: {
    ...typeTheme(theme, 'body2bold'),
    marginTop: theme.spacing(4),
  },
  schoolCandidatePaymentDividerContainer: {
    height: theme.spacing(4),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  schoolCandidatePaymentInfo: {
    color: theme.color.text.textSecondary,
  },
  schoolCandidatePaymentButtonText: {
    ...typeTheme(theme, 'buttonMedium'),
  },
  button: {
    marginBottom: theme.spacing(3),
    height: 'fit-content',
  },
  SingpassLoginButton: {
    width: '100%',
    height: 'fit-content',
    backgroundColor: '#E0242E', // special case: no token for this color (confirmed with designer)
    '&:hover': {
      backgroundColor: '#87070e',
    },
  },
  mimsLoginButton: {
    width: '100%',
    height: 'fit-content',
    color: 'rgba(0,0,0,0.87)',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    '&:hover': {
      color: 'rgba(0,0,0,0.87)',
      border: '1px solid rgba(0, 0, 0, 0.23)',
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  verticalDivider: {
    maxWidth: '100px',
    margin: '0px 20px 20px 0px',
    paddingRight: '20px',

    [theme.breakpoints.down('md')]: {
      maxWidth: '86px',
      margin: '0px 12px 12px 0px',
      paddingRight: '12px',
    },

    borderRight: `2px solid ${theme.color.border.borderMain}`,
  },
  lastLink: {
    borderWidth: 0,
  },
  individualLink: {
    fontWeight: 700,
    color: theme.color.text.textHeader,
    '&:hover': {
      color: theme.color.state.stateInfoDark,
    },
  },
  homeImageBg: {
    position: 'absolute',
    left: '0%',
    height: 615,
    bottom: 0,
    transform: 'translate(155px, 0px)',
    zIndex: 1,
    width: '100vw',
    backgroundColor: theme.color.background.bgAlt,
    borderTopLeftRadius: 40,
    boxShadow: theme.shadow.shadow2,
    [theme.breakpoints.down('sm')]: {
      height: 340,
      left: 'auto',
      right: '0%',
      width: 430,
    },
  },
  homeImage: {
    position: 'absolute',
    left: '0%',
    bottom: 0,
    height: 674,
    transform: 'translate(56px, 0px)',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      height: 'auto',
      width: '130%',
      transform: 'translate(25%, 0px)',
    },
  },
  rightContainer: {
    display: 'flex',
    position: 'relative',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(11),
    flex: 1,

    [theme.breakpoints.down('sm')]: {
      flex: 'none',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  leftContainer: {
    flex: 1,
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(11),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(4),
      paddingBottom: 0,
    },
  },
  announcementTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
  },
  errorInfo: {
    fontWeight: 'bold',
  },
}));

export interface LoginFormProps {
  isAnnouncement?: boolean;
  onAnnouncementOpen?: () => void;
  announcementNoticeDetails?: AnnouncementDetail;
  handleCloseAnnouncementModal: () => void;
  openAnnouncementModal?: boolean;
}

const { serverBusy } = RouteKey.error.child;
const { error: errorRoute, payment } = RouteKey.child;
const { schoolCandidatePayment, schoolCandidateAppealPayment } = RouteKey.payment.child;

const redirectIfServerBusy = (errorCode: string, history: any) => {
  if (errorCode === '1020') {
    history.push(`/${errorRoute}/${serverBusy}`);
  }
};

const LoginForm: FC<LoginFormProps> = (props: LoginFormProps) => {
  const {
    isAnnouncement = false,
    onAnnouncementOpen = () => null,
    announcementNoticeDetails,
    handleCloseAnnouncementModal,
    openAnnouncementModal = false,
  } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const { classes: guardClasses } = useWithGuardStyles();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ message: string | undefined; code: string | undefined }>();

  const { toForgetPassword } = useAuthNavigation();

  const loginForm = useLoginForm();

  const [oidcError, setOidcError] = useState<string | null>(null);
  {
    /*const [mimsError, setMimsError] = useState<string | null>(location?.state?.message || null);*/
  }

  const [mimsError, setMimsError] = useState<string | null>(
    ['4008', '1008', '1006', '1009'].includes(location?.state?.code ?? '')
      ? location?.state?.message || null
      : null
  );

  const { refetch: singPassConsentRefetch, status: singpassConsentStatus } = useGetSingPassConsent({
    onSuccess: (res) => {
      window.location.href = res.url;
      sessionStorage.setItem(
        'urlToBeRedirectedAfterLoggedIn',
        JSON.stringify(location.state || { from: { pathname: '/', search: '?' } })
      );
    },
    onError: (res) => {
      redirectIfServerBusy(res.response?.data?.code?.toString()!, history);
      setOidcError('Something wrong with SingPass.');
    },
  });

  const { refetch: mimsAuth, status: mimsAuthStatus } = useGetMIMSAuth({
    onSuccess: (res) => {
      window.location.href = res;
      sessionStorage.setItem(
        'urlToBeRedirectedAfterLoggedIn',
        JSON.stringify(location.state || { from: { pathname: '/', search: '?' } })
      );
    },
    onError: (res) => {
      redirectIfServerBusy(res.response?.data?.code?.toString()!, history);
      setOidcError('Something wrong with MIMS.');
    },
  });

  const { data: mimsResponse } = useGetMIMSStatus({});
  const {
    data: schoolCandidateAppealPaymentOpenPeriod,
  } = useGetSchoolCandidateAppealPaymentOpenPeriod({});
  const mimsAltLoginMsg = mimsResponse?.mimsAltLoginMsg ?? '';

  const { increaseFont, decreaseFont, isDisableIncrease, isDisableDecrease } = useHTMLFontSize();
  const [openPaymentPopUpModal, setOpenPaymentPopUpModal] = useState<boolean>(false);
  const [paymentPopUpURL, setPaymentPopUpURL] = useState<string>();

  useEffect(() => {
    if (
      loginForm.loginError &&
      loginForm.loginError !== t('profile->>LoginForm.MIMSAccountNotExisted') &&
      loginForm.loginError !== t('profile->>LoginForm.UserAccountIsLocked') &&
      loginForm.loginError !== t('profile->>LoginForm.AccountIsDisabled')
    ) {
      setMimsError('');
    }
  }, [loginForm.loginError]);

  return (
    <WidthGuard className={classes.passFlex}>
      <Box className={[classes.container, guardClasses.container].join(' ')}>
        <Box className={classes.leftContainer}>
          <Box className={classes.loginContainer}>
            <Box className={classes.loginForm}>
              <Grid container justify="space-between">
                <Grid xs={6} sm={12}>
                  <img
                    alt="seab logo"
                    src={isMobile ? SeabLogo : SeabLogoWithLabel}
                    height={90}
                    width={isMobile ? 80 : 414}
                    className={classes.logo}
                    style={{ float: 'left' }}
                  />
                </Grid>

                {isMobile && (
                  <Grid xs={6}>
                    <Suspense fallback={null}>
                      <RootFontControl
                        isHideAnnouncement={!isAnnouncement}
                        onClickAnnouncementButton={onAnnouncementOpen}
                        fontControlButtonGroupBackgroundColor="#B0CAE333"
                        isDisableDecreaseButton={isDisableDecrease}
                        isDisableIncreaseButton={isDisableIncrease}
                        onClickDecreaseFont={decreaseFont}
                        onClickIncreaseFont={increaseFont}
                      />
                    </Suspense>
                  </Grid>
                )}
              </Grid>
              <Typography className={classes.sectionText} variant="h1">
                {t('profile->>LoginForm.Log in to Candidates Portal')}
              </Typography>
              <OIDCLoginButton
                buttonClass={classes.SingpassLoginButton}
                id="singpass-login-button"
                status={singpassConsentStatus}
                onClick={singPassConsentRefetch}
                ariaLabel={t('profile->>LoginForm.Log in with Singpass')}
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <strong style={{ color: 'white' }}>Log in with</strong>
                    <img
                      src={SingpassLogo}
                      alt="singpass"
                      style={{ height: '1.1rem', marginTop: '4px' }}
                    />
                  </span>
                }
              />
              {mimsError && <ErrorInfo label={mimsError} />}
              {/* Conditionally render MIMS Login Button based on mimsAltLoginMsg */}
              {mimsAltLoginMsg === '' && (
                <OIDCLoginButton
                  buttonClass={classes.mimsLoginButton}
                  variant="outlined"
                  color="primary"
                  id="mims-login-button"
                  status={mimsAuthStatus}
                  onClick={mimsAuth}
                  ariaLabel={t('profile->>LoginForm.LogInWithMIMS')}
                  label={t('profile->>LoginForm.LogInWithMIMS')}
                />
              )}
              {/* Conditionally render the "OR" divider */}
              {mimsAltLoginMsg === '' && (
                <Box className={classes.orContainer}>
                  <Box>
                    <div className={classes.orWordContainer}>
                      <Typography variant="h2" className={classes.orWord}>
                        OR
                      </Typography>
                    </div>
                  </Box>
                </Box>
              )}
              <div className={classes.description2}>{mimsAltLoginMsg}</div>
              <br /> {/* Line break here */}
              <div className={classes.description}>
                {t('profile->>LoginForm.NonSingpassHolderLoginMsg')}
              </div>
              {loginForm.loginError && !mimsError && <ErrorInfo label={loginForm.loginError} />}
              <ErrorInfo
                className={classes.errorInfo}
                label={
                  (!loginForm.countDownObj.countDownEnded &&
                    t(loginForm.countDownError, {
                      timer: loginForm.countDownObj.countDown.format('s'),
                    })) ||
                  ''
                }
              />
              <ErrorInfo label={oidcError || ''} />
              <ExistingAccountLoginForm loginForm={loginForm} />
              {/* [EPPOPNKHJV-2678]: WOGAA Accessibility: fix "Elements with visible text labels do not have matching accessible names";
                  use the translated link text instead of hard-coded aria-label overrides. */}
              <Grid container xs={12}>
                <Grid className={classes.verticalDivider}>
                  <Link id="link-forgotUsername" href="/auth/forgot-username" withUnderline>
                    <Typography variant="caption" className={classes.individualLink}>
                      {t('profile->>LoginForm.Forgot Username')}
                    </Typography>
                  </Link>
                </Grid>

                {toForgetPassword && (
                  <Grid className={classes.verticalDivider}>
                    <Link id="link-forgotPassword" href="/auth/forgot-password" withUnderline>
                      <Typography variant="caption" className={classes.individualLink}>
                        {t('profile->>LoginForm.Forgot Password')}
                      </Typography>
                    </Link>
                  </Grid>
                )}

                <Grid className={classes.verticalDivider}>
                  <Link
                    id="link-activateAccount"
                    href="/auth/activate-account/detail"
                    withUnderline
                  >
                    <Typography variant="caption" className={classes.individualLink}>
                      {t('profile->>LoginForm.Activate Account')}
                    </Typography>
                  </Link>
                </Grid>

                <Grid className={[classes.verticalDivider, classes.lastLink].join(' ')}>
                  <Link id="link-unlockAccount" href="/auth/unlock-account" withUnderline>
                    <Typography variant="caption" className={classes.individualLink}>
                      {t('profile->>LoginForm.Unlock Account')}
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
              <Box className={classes.schoolCandidatePaymentDividerContainer}>
                <Box className={classes.orWordContainer} />
              </Box>
              {/* WOGAA Accessibility: payment buttons must use their visible text as their accessible names. */}
              <Button
                size="small"
                variant="contained"
                id="button-school-candidate-payment"
                className={classes.button}
                onClick={async () => {
                  await setPaymentPopUpURL(`/${payment}/${schoolCandidatePayment.route}`);
                  setOpenPaymentPopUpModal(true);
                }}
                fullWidth
              >
                <Typography variant="inherit" className={classes.schoolCandidatePaymentButtonText}>
                  {t('profile->>LoginForm.SchoolCandidatePaymentButtonLabel')}
                </Typography>
              </Button>
              {schoolCandidateAppealPaymentOpenPeriod?.openWindowPeriod && (
                <Button
                  size="small"
                  variant="contained"
                  id="button-school-candidate-appeal-fee"
                  className={classes.button}
                  onClick={async () => {
                    await setPaymentPopUpURL(`/${payment}/${schoolCandidateAppealPayment.route}`);
                    setOpenPaymentPopUpModal(true);
                  }}
                  fullWidth
                >
                  <Typography
                    variant="inherit"
                    className={classes.schoolCandidatePaymentButtonText}
                  >
                    {t('profile->>LoginForm.SchoolCandidateAppealPaymentButtonLabel')}
                  </Typography>
                </Button>
              )}
              <Typography
                variant="body2"
                className={[classes.description, classes.schoolCandidatePaymentInfo].join(' ')}
              >
                {t('profile->>LoginForm.SchoolCandidatePaymentMessage')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={classes.rightContainer}>
          {!isMobile && (
            <Box className={classes.homeImageBg}>
              <></>
            </Box>
          )}

          <img
            alt="enet"
            src={isMobile ? LoginContentImage : LoginContentImageWithOverlay}
            height={isMobile ? 445 : 610}
            className={classes.homeImage}
            loading={isMobile ? 'lazy' : 'eager'}
          />

          {!isMobile && (
            <Suspense fallback={null}>
              <RootFontControl
                isHideAnnouncement={!isAnnouncement}
                onClickAnnouncementButton={onAnnouncementOpen}
                fontControlButtonGroupBackgroundColor="#B0CAE333"
                isDisableDecreaseButton={isDisableDecrease}
                isDisableIncreaseButton={isDisableIncrease}
                onClickDecreaseFont={decreaseFont}
                onClickIncreaseFont={increaseFont}
              />
            </Suspense>
          )}
        </Box>
      </Box>
      {announcementNoticeDetails && (
        <Suspense fallback={null}>
          <Dialog open={openAnnouncementModal} maxWidth="lg">
            <DialogTitle>
              <Box className={classes.announcementTitleContainer}>
                {t('announcementsNotifications->>AnnouncementNoticeBoard.Notice')}
                <Box>
                  <IconButton
                    id="button-announcement-content-close"
                    name={IconName.cross}
                    iconSize="S"
                    color={theme.color.icon.iconDefault}
                    ariaLabel="announcement-content-close"
                    onClick={handleCloseAnnouncementModal}
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <AnnouncementSingleContent announcementNoticeDetails={announcementNoticeDetails} />
            </DialogContent>
          </Dialog>
        </Suspense>
      )}
      {paymentPopUpURL && (
        <PaymentAnnouncementContent
          openAnnouncementModal={openPaymentPopUpModal}
          setOpenAnnouncementModal={setOpenPaymentPopUpModal}
          redirectURL={paymentPopUpURL}
        />
      )}
    </WidthGuard>
  );
};

export default LoginForm;
